import React from 'react'
import { Box, Button, CircularProgress, Dialog, DialogContent, Typography } from '@mui/material'
import ChecklistIcon from '@mui/icons-material/Checklist'
import DeleteIcon from '@mui/icons-material/Delete'

// Select mode for the Tables tree (#1795): a toggle in the sidebar header
// that shows checkboxes, and the bar that deletes what is ticked. Both are
// controls of the page (data-less props), so Tables.tsx owns the state.

export const TocSelectToggle: React.FC<{ selectMode: boolean; onToggle: () => void }> = ({ selectMode, onToggle }) => (
  <Box sx={{ mb: 1 }}>
    <Button
      size="small"
      color="info"
      variant={selectMode ? 'contained' : 'outlined'}
      startIcon={<ChecklistIcon fontSize="small" />}
      onClick={onToggle}
      aria-pressed={selectMode}
    >
      {selectMode ? 'Done selecting' : 'Select'}
    </Button>
  </Box>
)

export const TocSelectBar: React.FC<{ count: number; busy: boolean; onDelete: () => void }> = ({ count, busy, onDelete }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1, px: 1, py: 0.5, borderRadius: 1, backgroundColor: 'action.hover' }}>
    <Typography variant="body2">{count} selected</Typography>
    <Button
      size="small"
      color="error"
      startIcon={<DeleteIcon fontSize="small" />}
      onClick={onDelete}
      disabled={count === 0 || busy}
      title={count === 0 ? 'Tick a table or folder to delete it.' : undefined}
      aria-label="Delete selected"
    >
      Delete
    </Button>
  </Box>
)

/** A delete in progress (#1809): a spinner and the count so far, so a big
 *  folder does not look frozen. It cannot be closed: the delete is running. */
export const TableDeleteProgress: React.FC<{ progress: { done: number; total: number } | null }> = ({ progress }) => (
  <Dialog open={!!progress} maxWidth="xs" fullWidth aria-labelledby="table-delete-progress">
    <DialogContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 3 }}>
      <CircularProgress color="info" size={28} />
      <Typography id="table-delete-progress" variant="body1" data-testid="table-delete-progress">
        {progress && progress.total > 0
          ? `Deleting ${progress.done.toLocaleString()} of ${progress.total.toLocaleString()} tables`
          : 'Deleting…'}
      </Typography>
    </DialogContent>
  </Dialog>
)
